import i18n from "../../../../utils/i18n";
import { getIndustrySelectMeta } from "../../utils/industrySelectOptions";

export const isGroupedProviderTiers = (value) =>
  Array.isArray(value) && value.length > 0 && value.every((tier) => Array.isArray(tier));

/** Passed to aspQuickResultSummary — entrepreneur + small_office + office (excludes corporate/enterprise). */
export const ASP_QUICK_RESULT_PACKAGE_SCOPE = {
  ENTREPRENEUR_AND_SMALL_OFFICE: "entrepreneur_and_small_office",
};

/** top3 [0] entrepreneur + [1] small_office — carousel; tier [2] is nagy könyvelőiroda. */
export const ASP_QUICK_RESULT_TIER_INDICES = [0, 1];

export const getAspQuickResultTiers = (potentialProviders) => {
  if (!isGroupedProviderTiers(potentialProviders)) {
    return potentialProviders?.length ? [potentialProviders] : [];
  }
  return ASP_QUICK_RESULT_TIER_INDICES.map((index) => potentialProviders[index] || []);
};

export const flattenAspQuickResultProviders = (potentialProviders) =>
  getAspQuickResultTiers(potentialProviders).flat();

export const countAspQuickResultProviders = (potentialProviders) => {
  const tiers = getAspQuickResultTiers(potentialProviders);
  if (tiers.length === 0) {
    return 0;
  }
  const ids = new Set();
  for (const tier of tiers) {
    for (const provider of tier || []) {
      const id = provider?.providerID ?? provider?.providerID2;
      if (id) {
        ids.add(String(id));
      }
    }
  }
  return ids.size;
};

const parseMatchScorePercent = (value) => {
  if (value === null || value === undefined) return 0;
  const n = parseInt(String(value).replace(/%/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
};

export const MIN_QUICK_RESULT_REVIEWS = 3;

/** Canonical provider identity — same order for dedupe and React keys. */
const getQuickResultProviderId = (provider) => {
  const id = provider?.providerID ?? provider?.providerID2;
  return id ? String(id) : "";
};

/**
 * Dedupe identity: id → name → list index (keeps distinct no-id/no-name rows).
 * Mirrors getQuickResultProviderKey fallbacks without per-carousel slotIndex.
 */
const getQuickResultProviderDedupeKey = (provider, listIndex = 0) => {
  const id = getQuickResultProviderId(provider);
  if (id) return id;
  const name = String(provider?.providerName || "").trim();
  if (name) return `name:${name}`;
  return `slot:${listIndex}`;
};

/** Non-empty trimmed nickname from providerofferstop3; empty when missing or not a string. */
export const getQuickResultProviderNickname = (provider) => {
  const nickname = provider?.nickname;
  if (typeof nickname !== "string") {
    return "";
  }
  return nickname.trim();
};

/** Stable React key segment; slotIndex disambiguates providers missing both ID fields. */
export const getQuickResultProviderDisplayName = (provider) => {
  const nickname = getQuickResultProviderNickname(provider);
  if (nickname) {
    return nickname;
  }
  const name = String(provider?.providerName || "").trim();
  return name || i18n.t("general.accountant");
};

export const getQuickResultProviderKey = (provider, slotIndex = 0) => {
  const id = getQuickResultProviderId(provider);
  if (id) return id;
  const name = String(provider?.providerName || "").trim();
  if (name) return `name:${name}:${slotIndex}`;
  return `slot:${slotIndex}`;
};

export const hasMinReviewCount = (provider, minReviews = MIN_QUICK_RESULT_REVIEWS) =>
  (provider?.reviewScores?.reviewNr ?? 0) >= minReviews;

export const hasProviderImage = (provider) =>
  Boolean(String(provider?.providerImage || "").trim());

export const hasProviderNickname = (provider) =>
  getQuickResultProviderNickname(provider).length > 0;

const rankProvidersByMatchScore = (providers) =>
  [...providers].sort(
    (a, b) =>
      parseMatchScorePercent(b.totalmatchscore) - parseMatchScorePercent(a.totalmatchscore),
  );

const dedupeProviders = (providers) => {
  const seen = new Set();
  return providers.filter((provider, index) => {
    const key = getQuickResultProviderDedupeKey(provider, index);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/** Only providers with at least 3 reviews, a profile image, and a nickname; empty array if none qualify. */
export const pickTopQuickResultProviders = (potentialProviders, limit = 9) => {
  const qualified = dedupeProviders(flattenAspQuickResultProviders(potentialProviders)).filter(
    (provider) =>
      hasMinReviewCount(provider, MIN_QUICK_RESULT_REVIEWS) &&
      hasProviderImage(provider) &&
      hasProviderNickname(provider),
  );

  if (qualified.length === 0) {
    return [];
  }

  return rankProvidersByMatchScore(qualified).slice(0, limit);
};

export const getIndustryLabel = (industryKey) => {
  if (!industryKey) return "";
  return i18n.t(`industrylabels.${industryKey}`);
};

export const getSelectedIndustryLabel = (selectedIndustry, industryInput) => {
  if (selectedIndustry === "other" && industryInput) {
    return industryInput;
  }
  return getIndustryLabel(selectedIndustry);
};

/** Same as dynamicListPages provider card account lifetime calculation. */
export const calculateAccountLifetime = (dateTime) => {
  const inputDate = new Date(dateTime);
  const currentDate = new Date();

  if (Number.isNaN(inputDate.getTime()) || inputDate > currentDate) {
    return { years: 0, months: 0 };
  }

  let yearsDiff = currentDate.getFullYear() - inputDate.getFullYear();
  let monthsDiff = currentDate.getMonth() - inputDate.getMonth();

  if (monthsDiff < 0) {
    yearsDiff -= 1;
    monthsDiff += 12;
  }

  return { years: yearsDiff, months: monthsDiff };
};

export const isNewBookkeepieAccount = (startDate) => {
  const lifetime = calculateAccountLifetime(startDate);
  return lifetime.years === 0 && lifetime.months === 0;
};

/** DLP card: "{N} hónapja|éve Bookkeepie könyvelő"; hidden for brand-new accounts. */
export const getProviderBookkeepieTenureLabel = (provider) => {
  const startDate = provider?.startDate;
  if (!startDate || isNewBookkeepieAccount(startDate)) {
    return "";
  }

  const { years, months } = calculateAccountLifetime(startDate);
  const durationPart =
    years < 1
      ? `${months} ${i18n.t("dlpproviderprofile.formonths")}`
      : `${years} ${i18n.t("dlpproviderprofile.foryears")}`;

  return `${durationPart} ${i18n.t("general.bkaccountant")}`;
};

export const getProviderReviewDisplay = (provider) => {
  const rawAvg =
    provider?.reviewScores?.reviewScoreAvg ?? provider?.providerReview ?? 0;
  const avg = Number(rawAvg);

  return {
    avg: Number.isFinite(avg) ? avg : 0,
    count: provider?.reviewScores?.reviewNr ?? 0,
  };
};

/** Industry keys from providerofferstop3 (score > 3). */
export const getProviderExperienceIndustryKeys = (provider) =>
  Array.isArray(provider?.providerExperienceIndustries)
    ? provider.providerExperienceIndustries
    : [];

/** Selected search-flow industry first; remaining keys keep backend order. */
export const orderIndustryKeysWithSelectedFirst = (keys, selectedIndustry) => {
  if (!Array.isArray(keys) || keys.length === 0) {
    return [];
  }
  const selected = String(selectedIndustry || "").trim();
  if (!selected) {
    return keys;
  }
  const selectedIndex = keys.indexOf(selected);
  if (selectedIndex <= 0) {
    return keys;
  }
  return [selected, ...keys.filter((key) => key !== selected)];
};

export const buildProviderExperienceTags = (
  provider,
  maxVisible = 4,
  selectedIndustry,
) => {
  const keys = orderIndustryKeysWithSelectedFirst(
    getProviderExperienceIndustryKeys(provider),
    selectedIndustry,
  );
  return {
    visible: keys.slice(0, maxVisible).map((key) => {
      const meta = getIndustrySelectMeta(key);
      return {
        key,
        emoji: meta.emoji,
        label: meta.title || getIndustryLabel(key),
      };
    }),
    overflow: Math.max(0, keys.length - maxVisible),
  };
};
